import { isChronicBillingEventBilled, parseChronicDescription } from "../../../utils/chronicBilling";
import { useEffect, useState } from "react";
import { API } from "../../../services/api";
import { Modal } from "./AdministrationFields";
import { ADMIN_API_CONFIG, EVENT_LABELS } from "../model/administrationConstants";
import { historyFieldLabel, historyItemCommentWithSource, isRobotHistoryItem, pdaCommentHistoryItems } from "../model/administrationHistory";
import { formatDate, formatDateTime, formatPatientName } from "../model/administrationRules";
import {
    chronicHistoryCommentStyle,
    chronicHistoryProductInfoStyle,
    emptyStyle,
    historyRowStyle,
    pdaHistoryCommentStyle,
    pdaHistoryEmptyStyle,
    pdaHistoryItemStyle,
    pdaHistoryMetaStyle,
    pdaHistoryPanelStyle,
    pdaHistoryTitleStyle,
} from "../styles/administrationModalStyles";

export function HistoryModal({ row, onClose }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        API.get(`/administration/patients/${row.patientId}/history`, ADMIN_API_CONFIG)
            .then(response => setHistory(response.data))
            .finally(() => setLoading(false));
    }, [row.patientId]);

    return (
        <Modal title={`Historique — ${formatPatientName(row)}`} onClose={onClose} width={760}>
            {loading ? <p style={emptyStyle}>Chargement…</p> : (
                <div style={{ maxHeight: "520px", overflowY: "auto" }}>
                    {history.length === 0 ? <p style={emptyStyle}>Aucune modification enregistrée.</p> : history.map(item => (
                        <div key={item.id} style={historyRowStyle}>
                            <div>
                                <strong>{historyFieldLabel(item.fieldName)}</strong>
                                <div style={{ color: "#6b7280", marginTop: "3px", overflowWrap: "anywhere" }}>
                                    {isRobotHistoryItem(item) ? (
                                        <>
                                            {item.newValue || "—"}
                                            {item.oldValue && <div style={{ marginTop: "3px", color: "#9ca3af", fontSize: "12px" }}>{item.oldValue}</div>}
                                        </>
                                    ) : (
                                        <>{item.oldValue || "—"} → {item.newValue || "—"}</>
                                    )}
                                </div>
                            </div>
                            <small style={{ color: "#9ca3af", whiteSpace: "nowrap" }}>
                                {item.changedAt ? new Date(item.changedAt).toLocaleString("fr-FR") : "Date inconnue"}<br />{item.changedBy || ""}
                            </small>
                        </div>
                    ))}
                </div>
            )}
        </Modal>
    );
}

export function RobotPdaHistoryPanel({ row, limit = 4, refreshKey = 0 }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        API.get(`/administration/patients/${row.patientId}/history`, ADMIN_API_CONFIG)
            .then(response => {
                if (!cancelled) setHistory(pdaCommentHistoryItems(response.data));
            })
            .catch(() => {
                if (!cancelled) setHistory([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [row.patientId, refreshKey]);

    if (loading) return <div style={pdaHistoryEmptyStyle}>Chargement de l'historique des commentaires...</div>;
    if (!history.length) return <div style={pdaHistoryEmptyStyle}>Aucun commentaire PDA pour ce patient.</div>;

    return (
        <div style={pdaHistoryPanelStyle}>
            <div style={pdaHistoryTitleStyle}>Historique commentaires PDA</div>
            {history.slice(0, limit).map(item => (
                <div key={item.id} style={pdaHistoryItemStyle}>
                    <div style={pdaHistoryCommentStyle}>{historyItemCommentWithSource(item)}</div>
                    <div style={pdaHistoryMetaStyle}>
                        {item.oldValue || historyFieldLabel(item.fieldName)}
                        {" · "}
                        {item.changedAt ? new Date(item.changedAt).toLocaleDateString("fr-FR") : "Date inconnue"}
                    </div>
                </div>
            ))}
        </div>
    );
}

export function RobotPdaHistoryModal({ row, onClose }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        API.get(`/administration/patients/${row.patientId}/history`, ADMIN_API_CONFIG)
            .then(response => setHistory(pdaCommentHistoryItems(response.data)))
            .catch(() => setHistory([]))
            .finally(() => setLoading(false));
    }, [row.patientId]);

    return (
        <Modal title={`Commentaires PDA — ${formatPatientName(row)}`} onClose={onClose} width={720}>
            {loading ? <p style={emptyStyle}>Chargement...</p> : (
                <div style={{ maxHeight: "520px", overflowY: "auto" }}>
                    {history.length === 0 ? <p style={emptyStyle}>Aucun commentaire PDA pour ce patient.</p> : history.map(item => (
                        <div key={item.id} style={historyRowStyle}>
                            <div>
                                <strong>{historyFieldLabel(item.fieldName)}</strong>
                                <div style={{ color: "#6b7280", marginTop: "3px", overflowWrap: "anywhere" }}>{historyItemCommentWithSource(item)}</div>
                                {item.oldValue && <div style={{ color: "#9ca3af", marginTop: "3px", fontSize: "12px" }}>{item.oldValue}</div>}
                            </div>
                            <small style={{ color: "#9ca3af", whiteSpace: "nowrap" }}>
                                {item.changedAt ? new Date(item.changedAt).toLocaleString("fr-FR") : "Date inconnue"}<br />{item.changedBy || ""}
                            </small>
                        </div>
                    ))}
                </div>
            )}
        </Modal>
    );
}

export function BillingHistoryModal({ row, onClose }) {
    const events = [...(row.billingEvents || [])]
        .filter(event => event.type !== "CHRONIC" || isChronicBillingEventBilled(event))
        .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || (b.id || 0) - (a.id || 0));

    return (
        <Modal title={`Historique facturation — ${formatPatientName(row)}`} onClose={onClose} width={760}>
            <div style={{ maxHeight: "520px", overflowY: "auto" }}>
                {events.length === 0 ? (
                    <p style={emptyStyle}>Aucune facturation enregistrée.</p>
                ) : events.map(event => {
                    const info = event.type === "CHRONIC" ? parseChronicDescription(event.description) : null;
                    return (
                        <div key={event.id} style={historyRowStyle}>
                            <div style={{ minWidth: 0 }}>
                                <strong>{EVENT_LABELS[event.type] || event.type}</strong>
                                <div style={{ color: "#64748b", marginTop: "3px" }}>Date produit : {formatDate(event.date)}</div>
                                {event.type === "CHRONIC" ? (
                                    <>
                                        <div style={chronicHistoryProductInfoStyle}>{info.productInfo || "Produit sans information"}</div>
                                        {info.reason && <small style={{ color: "#4f46e5", fontWeight: 800 }}>Motif : {info.reason}</small>}
                                        {info.comment && <small style={chronicHistoryCommentStyle}>Commentaire : {info.comment}</small>}
                                    </>
                                ) : event.description && (
                                    <div style={{ color: "#4b5563", marginTop: "3px" }}>{event.description}</div>
                                )}
                                <small style={{ display: "block", color: "#9ca3af", marginTop: "4px" }}>
                                    Mis en facturation le {formatDateTime(event.createdAt) || "date inconnue"} par {event.createdBy || "utilisateur inconnu"}
                                </small>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
}
